package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.comparison.ComparisonMetric
import com.evoapps.evoshift.domain.comparison.DateRange
import com.evoapps.evoshift.domain.comparison.SalesComparisons
import com.evoapps.evoshift.domain.comparison.SalesStatistics
import com.evoapps.evoshift.domain.comparison.StatisticsBase
import com.evoapps.evoshift.domain.comparison.StatisticsPeriodKind
import com.evoapps.evoshift.domain.comparison.StatisticsQuery
import com.evoapps.evoshift.domain.market.Market
import com.evoapps.evoshift.domain.market.payroll.PayrollRules
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.DailyRecord
import com.evoapps.evoshift.domain.model.DayWeather
import com.evoapps.evoshift.domain.model.DaySpecials
import com.evoapps.evoshift.domain.model.Workplace
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.usecase.finance.GetSalesComparisonUseCase
import com.evoapps.evoshift.domain.usecase.specialdays.ObserveSpecialDaysUseCase
import com.evoapps.evoshift.domain.usecase.weather.ObserveWorkplaceWeatherUseCase
import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.minus
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn

/*
 * SH-228: the Statistics screen, opened from the Money screen's bar: a period (week, month or a
 * custom range) against the period before or the same period last year (by weekday, or by date),
 * for one metric at a time (net sales first). Money only, like the Money screen: without the
 * workplace's finances nothing is read. The SH-225 engine does the arithmetic; this view model
 * reads what a period needs (one range query per run, see GetSalesComparisonUseCase) and keeps
 * every day read while the screen is open, so going back and forth reads nothing twice.
 */

data class StatisticsUiState(
    val workplace: Workplace? = null,
    val capabilities: Set<Capability> = emptySet(),
    /** Capabilities were read: before that, nothing is shown (not even "no access"). */
    val checked: Boolean = false,
    val today: LocalDate? = null,
    val kind: StatisticsPeriodKind = StatisticsPeriodKind.MONTH,
    val period: DateRange? = null,
    val base: StatisticsBase = StatisticsBase.PREVIOUS_PERIOD,
    val byCalendarDate: Boolean = false,
    val metric: ComparisonMetric = ComparisonMetric.NET_SALES,
    /** Every stored day read so far, by date, as its daily result counts it. */
    val records: Map<LocalDate, DailyRecord> = emptyMap(),
    /** The runs of days already read (a run is never read twice). */
    val readRanges: List<DateRange> = emptyList(),
    /** True while the current question's days are being read (the figures shown are the last ones). */
    val isLoading: Boolean = false,
    /** The days could not be read: Try again instead of empty figures. */
    val loadError: UiText? = null,
    /** SH-226: each day's weather (the period and its base), when the workplace has weather on. */
    val weather: Map<String, DayWeather> = emptyMap(),
    /** SH-227: each day's events and special days. */
    val specialDays: Map<String, DaySpecials> = emptyMap()
) {
    /** Only the owner and financial managers see the statistics (the Money screen's gate). */
    val allowed: Boolean get() = capabilities.shows(Gate.MONEY)

    val firstDayOfWeek: Int get() = workplace?.settings?.firstDayOfWeek ?: 1

    val currency: String get() = workplace?.settings?.currency?.takeIf { it.isNotBlank() } ?: DEFAULT_CURRENCY

    val query: StatisticsQuery?
        get() = period?.let { StatisticsQuery(it, kind, base, byCalendarDate, firstDayOfWeek) }

    /** Whether every day [query] needs has been read. */
    val ready: Boolean
        get() {
            val needed = query?.readRanges ?: return false
            return needed.all { range -> range.dates.all { date -> readRanges.any { date in it } } }
        }

    /** The figures of [query] from the days read (null before the period is known). */
    val statistics: SalesStatistics? by lazy {
        val q = query ?: return@lazy null
        val holidays = PayrollRules.of(workplace?.settings?.market ?: Market.POLAND)
        SalesStatistics(q, SalesComparisons(records.values, holidays::isHoliday))
    }

    /** The period can move forward: never past the period holding today. */
    val canGoForward: Boolean
        get() {
            val p = period ?: return false
            val t = today ?: return true
            return p.end < t
        }
}

sealed class StatisticsEvent {
    data class SelectKind(val kind: StatisticsPeriodKind) : StatisticsEvent()
    /** The period before (-1) or after (+1), of the same kind. */
    data class Move(val steps: Int) : StatisticsEvent()
    /** A custom range (capped to [StatisticsQuery.MAX_DAYS] days). */
    data class SetCustomRange(val start: LocalDate, val end: LocalDate) : StatisticsEvent()
    data class SelectBase(val base: StatisticsBase) : StatisticsEvent()
    /** Last year by calendar date (true) or by weekday (false). */
    data class SetByCalendarDate(val byDate: Boolean) : StatisticsEvent()
    data class SelectMetric(val metric: ComparisonMetric) : StatisticsEvent()
    data object Retry : StatisticsEvent()
}

class StatisticsViewModel(
    private val workplaceId: String,
    /** A day of the month the screen opens on ("YYYY-MM-DD", the Money screen's month); null: today. */
    private val anchor: String?,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val workplaceRepository: WorkplaceRepository,
    private val getSalesComparison: GetSalesComparisonUseCase,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    private val observeWeather: ObserveWorkplaceWeatherUseCase? = null,
    private val observeSpecialDays: ObserveSpecialDaysUseCase? = null,
    private val today: () -> LocalDate = { AppClock.todayIn(TimeZone.currentSystemDefault()) }
) : ViewModel() {

    private val _uiState = MutableStateFlow(StatisticsUiState())
    val uiState: StateFlow<StatisticsUiState> = _uiState.asStateFlow()

    private var readJob: Job? = null
    private var markersJob: Job? = null

    init {
        start()
    }

    private fun start() {
        viewModelScope.launch {
            val capabilities = capabilitiesAt(getCurrentUserUseCase(), observePlanActive, workplaceId).first()
            val now = today()
            _uiState.update { it.copy(capabilities = capabilities, checked = true, today = now) }
            // Money only: nothing is read otherwise (the rules would refuse it anyway).
            if (!capabilities.shows(Gate.MONEY)) return@launch
            val workplace = workplaceRepository.getWorkplace(workplaceId).getOrElse { e ->
                _uiState.update { it.copy(loadError = e.toUiText()) }
                return@launch
            }
            val day = anchor?.let { runCatching { LocalDate.parse(it) }.getOrNull() }?.let { minOf(it, now) } ?: now
            _uiState.update { it.copy(workplace = workplace, period = DateRange.monthOf(day)) }
            refresh()
        }
    }

    fun onEvent(event: StatisticsEvent) {
        val state = _uiState.value
        val period = state.period
        when (event) {
            is StatisticsEvent.SelectKind -> {
                if (event.kind == state.kind || period == null) return
                // The new period holds the last day of the current one that is not after today.
                val reference = minOf(period.end, state.today ?: period.end)
                val next = when (event.kind) {
                    StatisticsPeriodKind.WEEK -> StatisticsQuery.week(reference, state.firstDayOfWeek)
                    StatisticsPeriodKind.MONTH -> StatisticsQuery.month(reference)
                    // A custom range starts as the last 14 days up to the reference.
                    StatisticsPeriodKind.CUSTOM -> StatisticsQuery.custom(reference.minusDays(13), reference)
                }
                _uiState.update { it.copy(kind = event.kind, period = next) }
            }
            is StatisticsEvent.Move -> {
                if (period == null || (event.steps > 0 && !state.canGoForward)) return
                _uiState.update { it.copy(period = StatisticsQuery.shift(period, state.kind, event.steps)) }
            }
            is StatisticsEvent.SetCustomRange ->
                _uiState.update { it.copy(kind = StatisticsPeriodKind.CUSTOM, period = StatisticsQuery.custom(event.start, event.end)) }
            is StatisticsEvent.SelectBase -> _uiState.update { it.copy(base = event.base) }
            is StatisticsEvent.SetByCalendarDate -> _uiState.update { it.copy(byCalendarDate = event.byDate) }
            // The metric reads nothing: the days are the same.
            is StatisticsEvent.SelectMetric -> {
                _uiState.update { it.copy(metric = event.metric) }
                return
            }
            StatisticsEvent.Retry -> {
                _uiState.update { it.copy(loadError = null) }
                if (state.workplace == null) {
                    start()
                    return
                }
            }
        }
        refresh()
    }

    /** Reads what the question needs and is not read yet (one range query per run); follows its days' weather and special days. */
    private fun refresh() {
        val state = _uiState.value
        val query = state.query ?: return
        val workplace = state.workplace ?: return
        followMarkers(workplace, query)
        if (state.ready) {
            _uiState.update { it.copy(isLoading = false) }
            return
        }
        readJob?.cancel()
        _uiState.update { it.copy(isLoading = true, loadError = null) }
        readJob = viewModelScope.launch {
            // Only the runs not read yet (going back to the period before reads nothing).
            val missing = query.readRanges.filter { range -> range.dates.any { date -> state.readRanges.none { date in it } } }
            getSalesComparison.statisticsRecords(workplaceId, query, only = missing).fold(
                onSuccess = { records ->
                    _uiState.update { s ->
                        s.copy(
                            records = s.records + records.mapNotNull { r -> runCatching { LocalDate.parse(r.date) to r }.getOrNull() },
                            readRanges = s.readRanges + missing,
                            isLoading = false
                        )
                    }
                },
                onFailure = { e -> _uiState.update { it.copy(isLoading = false, loadError = e.toUiText()) } }
            )
        }
    }

    private var followed: Pair<DateRange, DateRange?>? = null

    /** SH-226/SH-227: the period's and its base's weather and special days, live. */
    private fun followMarkers(workplace: Workplace, query: StatisticsQuery) {
        val key = query.period to query.baseRange
        if (followed == key) return
        followed = key
        markersJob?.cancel()
        val weather = observeWeather
        val specials = observeSpecialDays
        if (weather == null && specials == null) return
        val ranges = listOfNotNull(query.period, query.baseRange)
        markersJob = viewModelScope.launch {
            ranges.forEach { range ->
                launch {
                    weather?.invoke(workplace, range.start, range.end)?.collect { days ->
                        _uiState.update { it.copy(weather = it.weather + days.mapKeys { (date, _) -> date.toString() }) }
                    }
                }
                launch {
                    specials?.invoke(workplace, range.start, range.end)?.collect { snapshot ->
                        _uiState.update { it.copy(specialDays = it.specialDays + snapshot.days.mapKeys { (date, _) -> date.toString() }) }
                    }
                }
            }
        }
    }
}

private fun LocalDate.minusDays(days: Int): LocalDate = minus(DatePeriod(days = days))
